import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, AlarmClock } from "lucide-react";
import { supabase } from "../lib/supabase";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";
import { CHANNEL_LABEL } from "../lib/format";

interface Alert {
  id: string;
  run_date: string;
  channel: string;
  target_name: string | null;
  target_id: string | null;
  category: string;
  metric: string;
  observed_value: number;
  target_value: number;
  threshold_value: number;
  direction: "above_max" | "below_min";
  severity: "info" | "warning" | "critical";
  message: string;
  status: "open" | "acknowledged" | "resolved" | "snoozed" | "expired";
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  created_at: string;
}

const SEVERITY: Record<string, { label: string; cls: string; icon: typeof AlertTriangle }> = {
  critical: { label: "Crítico",  cls: "bg-[#fee2e2] text-[#991b1b] border-[#fca5a5]", icon: AlertTriangle },
  warning:  { label: "Atenção",  cls: "bg-[#fef3c7] text-[#92400e] border-[#fde68a]", icon: AlertTriangle },
  info:     { label: "Info",     cls: "bg-[#dbeafe] text-[#1e40af] border-[#bfdbfe]", icon: AlertTriangle },
};

const METRIC_LABEL: Record<string, string> = {
  cpm_brl: "CPM", cpc_brl: "CPC", cpa_brl: "CPA", cpl_brl: "CPL", ctr_pct: "CTR", roas_real: "ROAS real",
};

function fmtVal(metric: string, v: number): string {
  if (metric.endsWith("_brl")) return `R$ ${v.toFixed(2).replace(".", ",")}`;
  if (metric.endsWith("_pct")) return `${v.toFixed(2).replace(".", ",")}%`;
  if (metric === "roas_real") return `${v.toFixed(2).replace(".", ",")}x`;
  return String(v);
}

export default function Alerts() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [filter, setFilter] = useState<"open" | "all">("open");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      let q = supabase.from("ads_alerts").select("*").order("created_at", { ascending: false }).limit(200);
      if (filter === "open") q = q.eq("status", "open");
      const { data } = await q;
      setAlerts((data as Alert[]) ?? []);
      setLoading(false);
    };
    load();
    const ch = supabase.channel("alerts-changes").on("postgres_changes", { event: "*", schema: "public", table: "ads_alerts" }, load).subscribe();
    return () => { ch.unsubscribe(); };
  }, [filter]);

  const ack = async (id: string) => {
    await supabase.from("ads_alerts").update({ status: "acknowledged", acknowledged_by: "dashboard", acknowledged_at: new Date().toISOString() }).eq("id", id);
  };
  const resolve = async (id: string) => {
    await supabase.from("ads_alerts").update({ status: "resolved", acknowledged_by: "dashboard", acknowledged_at: new Date().toISOString() }).eq("id", id);
  };
  const snooze = async (id: string) => {
    await supabase.from("ads_alerts").update({ status: "snoozed" }).eq("id", id);
  };

  const grouped = alerts.reduce<{ critical: Alert[]; warning: Alert[]; info: Alert[] }>((acc, a) => {
    acc[a.severity].push(a);
    return acc;
  }, { critical: [], warning: [], info: [] });

  return (
    <>
      <PageHeader
        title="Alertas"
        subtitle={`${alerts.length} alerta${alerts.length === 1 ? "" : "s"} disparado${alerts.length === 1 ? "" : "s"} pela comparação com benchmarks`}
        icon={<AlertTriangle size={22} strokeWidth={2} />}
        actions={
          <div className="flex gap-1 bg-white border border-esm-gray-100 rounded-xl p-1 shadow-card">
            {(["open", "all"] as const).map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${filter === f ? "bg-esm-brown-500 text-white" : "text-esm-gray-700 hover:bg-esm-cream-50"}`}>
                {f === "open" ? "Abertos" : "Todos"}
              </button>
            ))}
          </div>
        }
      />

      {loading ? (
        <p className="text-esm-gray-400 italic">Carregando…</p>
      ) : alerts.length === 0 ? (
        <Card>
          <div className="py-10 text-center">
            <p className="text-4xl mb-3">✅</p>
            <p className="font-bold text-esm-gray-900 text-lg">Tudo em ordem</p>
            <p className="text-sm text-esm-gray-400 mt-1">Nenhum benchmark violado. O agente roda às 08h e checa contra os parâmetros.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {(["critical", "warning", "info"] as const).map((sev) => (
            grouped[sev].length > 0 && (
              <Section key={sev} severity={sev} alerts={grouped[sev]} onAck={ack} onResolve={resolve} onSnooze={snooze} />
            )
          ))}
        </div>
      )}
    </>
  );
}

function Section({ severity, alerts, onAck, onResolve, onSnooze }: { severity: "critical" | "warning" | "info"; alerts: Alert[]; onAck: (id: string) => void; onResolve: (id: string) => void; onSnooze: (id: string) => void }) {
  const conf = SEVERITY[severity]!;
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-esm-gray-400 pl-1">{conf.label} ({alerts.length})</h3>
      <div className="grid lg:grid-cols-2 gap-3">
        {alerts.map((a) => <AlertCard key={a.id} a={a} onAck={onAck} onResolve={onResolve} onSnooze={onSnooze} />)}
      </div>
    </div>
  );
}

function AlertCard({ a, onAck, onResolve, onSnooze }: { a: Alert; onAck: (id: string) => void; onResolve: (id: string) => void; onSnooze: (id: string) => void }) {
  const conf = SEVERITY[a.severity]!;
  const Icon = conf.icon;
  const isOpen = a.status === "open";
  return (
    <div className={`rounded-2xl border p-4 shadow-card ${conf.cls.replace("bg-", "border-").replace(/text-\S+/, "")} bg-white`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-xl ${conf.cls} flex items-center justify-center shrink-0`}>
          <Icon size={18} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-esm-cream-50 text-esm-brown-700 border border-esm-brown-100">{CHANNEL_LABEL[a.channel]}</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-esm-gray-100 text-esm-gray-700">{a.category}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${conf.cls}`}>{conf.label}</span>
            {!isOpen && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-esm-gray-100 text-esm-gray-700">{a.status}</span>}
          </div>
          <p className="font-bold text-esm-gray-900 text-base leading-tight">{a.target_name ?? a.target_id ?? "(sem alvo)"}</p>
          <p className="text-[11px] text-esm-gray-400 mt-0.5">Métrica: {METRIC_LABEL[a.metric] ?? a.metric} · {a.run_date}</p>

          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="bg-esm-cream-50 rounded-lg py-2">
              <p className="text-[10px] uppercase tracking-wider font-bold text-esm-gray-400">Observado</p>
              <p className="text-sm font-bold text-esm-brown-900 tabular-nums">{fmtVal(a.metric, a.observed_value)}</p>
            </div>
            <div className="bg-esm-cream-50 rounded-lg py-2">
              <p className="text-[10px] uppercase tracking-wider font-bold text-esm-gray-400">Ideal</p>
              <p className="text-sm font-bold text-esm-gray-900 tabular-nums">{fmtVal(a.metric, a.target_value)}</p>
            </div>
            <div className="bg-esm-cream-50 rounded-lg py-2">
              <p className="text-[10px] uppercase tracking-wider font-bold text-esm-gray-400">Limite</p>
              <p className="text-sm font-bold text-esm-gray-900 tabular-nums">{fmtVal(a.metric, a.threshold_value)}</p>
            </div>
          </div>

          <p className="text-sm text-esm-gray-700 mt-3">{a.message}</p>

          {isOpen && (
            <div className="flex gap-2 mt-3">
              <button onClick={() => onAck(a.id)} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-bold px-2 py-1.5 rounded-lg bg-esm-brown-500 hover:bg-esm-brown-700 text-white transition-colors">
                <CheckCircle2 size={12}/> Reconhecer
              </button>
              <button onClick={() => onResolve(a.id)} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-bold px-2 py-1.5 rounded-lg bg-esm-success/10 hover:bg-esm-success hover:text-white text-esm-success border border-esm-success/30 transition-colors">
                <CheckCircle2 size={12}/> Resolver
              </button>
              <button onClick={() => onSnooze(a.id)} className="flex items-center justify-center gap-1.5 text-xs font-bold px-2 py-1.5 rounded-lg bg-esm-gray-100 hover:bg-esm-gray-200 text-esm-gray-700 transition-colors">
                <AlarmClock size={12}/>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
